/*
 * A group, as the rest of the interface reads it: which kind it is, what it is called, and how the
 * person family draws it.
 *
 * Three kinds share one wire shape (`GroupView.builtin`). An ordinary group is made and named by
 * people. The three **special** groups exist by themselves: Everyone (`@all`, every person, guests
 * included), Team members (`@team`, everyone who is not a guest) and Administrators (`@admins`). The
 * server keeps their rows and decides standing from them; only an administrator changes Team members
 * and Administrators, and nobody changes Everyone by hand. Until somebody renames a special its name
 * is this build's copy rather than data — the server stores the handle there, which is not a name a
 * person should read. A **personal** group holds exactly one account and exists so that "only me" can
 * be said wherever a set of groups is chosen; it is not a group anybody manages, mentions or browses,
 * so the directory never lists it and it is always drawn as its person.
 */

import { t, type PlainKey } from "../i18n/index.ts"
import type { ChipPerson } from "../people/PersonChip.tsx"
import type { GroupId, GroupView } from "../store/model.ts"

/**
 * What deciding a group's kind and name needs, and no more, so that a surface that holds a narrower
 * row than the whole view — the composer's mention source — can ask too.
 */
export type NamedGroup = Pick<GroupView, "builtin" | "handle" | "displayName">

/**
 * The special groups, in the directory's order: widest first, so the list reads from everybody down
 * to the few who run the instance.
 */
export const SPECIAL_GROUPS = ["all", "team", "admins"] as const satisfies readonly NonNullable<
	GroupView["builtin"]
>[]
export type SpecialGroup = (typeof SPECIAL_GROUPS)[number]

/** The words a special group is drawn with. */
export interface SpecialWords {
	/** Its name, until somebody renames it. */
	name: PlainKey
	/** Who decides its members, on its card's second line. */
	rule: PlainKey
	/** The same, said in full under its page's head. */
	explain: PlainKey
}

/** Each special's words, one entry per special, so a new special does not compile without them. */
export const SPECIAL_WORDS: Record<SpecialGroup, SpecialWords> = {
	all: {
		name: "groups.special.all.name",
		rule: "groups.special.all.rule",
		explain: "groups.special.all.explain",
	},
	team: {
		name: "groups.special.team.name",
		rule: "groups.special.team.rule",
		explain: "groups.special.team.explain",
	},
	admins: {
		name: "groups.special.admins.name",
		rule: "groups.special.admins.rule",
		explain: "groups.special.admins.explain",
	},
}

/** Which special group this is, or undefined for an ordinary or a personal group. */
export function specialKindOf(group: NamedGroup): SpecialGroup | undefined {
	const kind = group.builtin
	return (SPECIAL_GROUPS as readonly string[]).includes(kind ?? "")
		? (kind as SpecialGroup)
		: undefined
}

/** Whether this is one of the three special groups. */
export function isSpecialGroup(group: NamedGroup): boolean {
	return specialKindOf(group) !== undefined
}

/**
 * The special group of this kind in the model, or undefined while the model does not hold it — a
 * caller that offers a change on a special offers nothing then, rather than guess an id.
 */
export function specialGroupOf(
	groups: Readonly<Record<GroupId, GroupView>>,
	kind: SpecialGroup,
): GroupView | undefined {
	return Object.values(groups).find((group) => group.builtin === kind)
}

/** Whether this is Team members, the special group every team member belongs to. */
export function isTeamGroup(group: NamedGroup): boolean {
	return group.builtin === "team"
}

/**
 * Whether this is Everyone (`@all`), the special group every person is in, guests included. Through it
 * only team members could own an AI chat, which Team members says truthfully, so the AI chats' picker
 * offers it only where a chat already holds it.
 */
export function isEveryoneGroup(group: NamedGroup): boolean {
	return group.builtin === "all"
}

/** Whether this is somebody's one-person group. */
export function isPersonalGroup(group: NamedGroup): boolean {
	return group.builtin === "personal"
}

/**
 * Whether the groups directory shows this group. Personal groups are left out wherever groups are
 * browsed, whichever way they reached the client: a list of a hundred one-person groups would bury
 * the handful anybody opens the page for.
 */
export function isDirectoryGroup(group: GroupView): boolean {
	return !isPersonalGroup(group)
}

/**
 * The name a group is read by. A special carries the copy's name until somebody gives it one of its
 * own; every other group carries its own. A personal group's name is its person's, which the caller
 * draws from the account (`GroupPicker`), so it never reaches this function.
 */
export function groupName(group: NamedGroup): string {
	const kind = specialKindOf(group)
	if (kind !== undefined && (group.displayName === "" || group.displayName === group.handle)) {
		return t(SPECIAL_WORDS[kind].name)
	}
	return group.displayName
}

/** How the folded comparison of two group names treats case and the spaces around a name. */
function foldName(name: string): string {
	return name.trim().toLocaleLowerCase("en-GB")
}

/**
 * Whether an ordinary group's display name would read as a special's copy name — "Team members", in
 * any case, with spaces around it. Two groups of that name in a picker would be told apart only by
 * their handles, so the New group form and a rename refuse it. The server does not: the copy names
 * live in this build's words, never in Go.
 */
export function isSpecialGroupName(name: string): boolean {
	const folded = foldName(name)
	return (
		folded !== "" && SPECIAL_GROUPS.some((kind) => foldName(t(SPECIAL_WORDS[kind].name)) === folded)
	)
}

/** A group's place among the specials, or past the last of them for any other group. */
function specialRank(group: NamedGroup): number {
	const kind = specialKindOf(group)
	return kind === undefined ? SPECIAL_GROUPS.length : SPECIAL_GROUPS.indexOf(kind)
}

/** The directory's order: the specials first, widest first, then the rest by name and handle. */
export function byGroupName(a: GroupView, b: GroupView): number {
	return (
		specialRank(a) - specialRank(b) ||
		groupName(a).localeCompare(groupName(b)) ||
		a.handle.localeCompare(b.handle)
	)
}

/**
 * The group as a `ChipPerson`: people and groups are two lists of the same kind of thing — a picture,
 * a name, a handle, a quiet second line — so a group is handed to `PersonChip`/`PersonHead` rather
 * than drawn by a second component that would drift from the first (X-A3 P7). The id is **zero** on
 * purpose: a ramp colour stands for one account, so a group keeps the neutral initials square and no
 * hover card opens over it (`core/people/PersonChip.tsx`).
 */
export function chipGroup(group: GroupView): ChipPerson {
	return { id: 0, name: groupName(group), handle: group.handle }
}
